export interface RouteConfigProps {
  path?: string;
  component?: null | string;
  title: string;
  label?: string;
  iconPath?: string;
  notSidebar?: boolean; // boolean (whether or not to show in sidebar)
  children?: Array<RouteConfigProps>;
  key?: string;
  tabs?: number;
  onClick?: () => void | Promise<void>;
}

export function buildRouteConfig(
  config: Array<RouteConfigProps>
): Array<RouteConfigProps> {
  const finalConfig: Array<RouteConfigProps> = [];

  const generateRoutes = (
    route: RouteConfigProps,
    parentPath: string = "/"
  ) => {
    // Skip routes without a path (e.g., onClick-only items)
    if (!route.path) {
      return;
    }

    const fullPath = route.path === "/" ? "/" : `${parentPath}${route.path}/`;

    const { children, ...routeWithoutChildren } = route;
    const routeWithFullPath = {
      ...routeWithoutChildren,
      path: fullPath,
      key: fullPath
    };

    if (route.component) {
      finalConfig.push(routeWithFullPath);
    }

    if (children) {
      children.forEach((child) => generateRoutes(child, fullPath));
    }
  };

  config.forEach((route) => generateRoutes(route));

  return finalConfig;
}

export const buildMainLinksData = (config: Array<RouteConfigProps>) => {
  return config
    .filter((item) => item.children !== undefined && !item.notSidebar)
    .map((item) => {
      // If all children are notSidebar, provide a direct path for icon navigation
      const visibleChildren = (item.children || []).filter((c) => !c.notSidebar);
      const firstChild = (item.children || [])[0];
      const directPath =
        visibleChildren.length === 0 && item.path
          ? firstChild?.path
            ? `/${item.path}/${firstChild.path}`
            : `/${item.path}`
          : undefined;
      return {
        iconPath: item.iconPath,
        title: item.title,
        directPath,
      };
    });
};

export const buildLinksData = (config: Array<RouteConfigProps>) => {
  const finalConfig: Record<
    string,
    Array<{
      title: string;
      label: string;
      path?: string;
      tabs?: number;
      isLabel?: boolean;
      parentId?: string; // Add parentId to track parent-child relationships
      onClick?: () => void | Promise<void>;
    }>
  > = {};

  // Set keys
  config
    .filter((item) => item.iconPath !== undefined)
    .map((item) => {
      finalConfig[item.title] = [];
    });

  // Set values
  config
    .filter((item) => item.component !== null)
    .filter((item) => item.iconPath !== undefined)
    .map((item) => {
      finalConfig[item.title].push({
        title: item.title,
        label: item.title,
        path: item.path === "/" ? "/" : "/" + item.path,
        tabs: item.tabs
      });
    });

  // Set child values
  config
    .filter((item) => item.component !== undefined)
    .forEach((item) => {
      if (item.children) {
        const processChildren = (
          children: RouteConfigProps[],
          parentPath: string
        ) => {
          children.forEach((child) => {
            if (child.notSidebar) return;
            // Find parent ID if this is a child of a label
            let parentId;
            if (child.component === null) {
              // This is a label, no parent needed
              parentId = undefined;
            } else {
              // Look for the most recent label in the same level
              for (let i = finalConfig[item.title].length - 1; i >= 0; i--) {
                const potentialParent = finalConfig[item.title][i];
                if (potentialParent.isLabel) {
                  parentId = potentialParent.title;
                  break;
                }
              }
            }

            finalConfig[item.title].push({
              title: child.title,
              label: child.label || child.title,
              path: child.path ? `${parentPath}/${child.path}` : undefined,
              tabs: child.tabs,
              isLabel: child.component === null,
              parentId: parentId,
              onClick: child.onClick
            });
            if (child.children && child.path) {
              processChildren(child.children, `${parentPath}/${child.path}`);
            }
          });
        };
        processChildren(item.children, `/${item.path}`);
      }
    });

  return finalConfig;
};
